import { Component, OnInit, OnDestroy, Input } from '@angular/core'
import { Contact } from "@models/contact"
import { Address } from "@models/address"
import { Phone } from "@models/phone"
import { AlternateEmail } from "@models/alternate-email"
import { Store, Action } from "@ngrx/store"
import { AppState } from "@store/app-state"
import { ContactEffects } from "@reducers/effects/contact"
import {
  RESET_CONTACT_ERRORS,
  CREATE_CONTACT_FAILURE,
  ADD_CONTACT,
  RESET_EDITABLE_CONTACT,
  UPDATE_CONTACT,
  REPLACE_CONTACT,
  CREATE_CONTACT
} from "@reducers/contacts"
import { ModalService } from "@services/modal"
import { LocalData, CompleterService, CompleterData, CompleterItem } from 'ng2-completer'
import { ContactsStore } from "@store/contacts"
import { Observable } from "rxjs/Observable"
import {
  CONTACT_TYPES,
  CONTACT_INSTALMENTS,
  CONTACT_GRADES,
  CONTACT_GENDERS,
  CONTACT_SALUTATIONS,
  CONTACT_STATES,
  CONTACT_PHONE_LABELS,
  CONTACT_MARKETING_SOURCES
} from "@constants/contacts"
import { SCHOOLS } from "@constants/schools"
import { ConstData, ConstantsHelpers } from "@constants/helpers"

@Component({
  selector: 'contact-form',
  templateUrl: './contact-form.pug',
})
export class ContactFormComponent implements OnInit, OnDestroy {

  private readonly guardianUrl: string = "guardians?query="

  @Input() public isInEditMode: boolean = false

  public contactsStore: Observable<ContactsStore>
  public contact: Contact

  public salutationSearchStr: string
  public salutationCompleter: CompleterData
  public salutations: ConstData[] = CONTACT_SALUTATIONS

  public genderSearchStr: string
  public gendersCompleter: CompleterData
  public genders: ConstData[] = CONTACT_GENDERS

  public stateSearchStr: string[] = []
  public stateCompleter: CompleterData
  public states: ConstData[] = CONTACT_STATES

  public phoneLabelSearchStr: string[] = []
  public phoneLabelCompleter: CompleterData
  public phoneLabels: ConstData[] = CONTACT_PHONE_LABELS

  public typeSearchStr: string
  public typesCompleter: CompleterData
  public types: ConstData[] = CONTACT_TYPES

  public instalmentsCompleter: CompleterData
  public instalments: ConstData[] = CONTACT_INSTALMENTS

  public gradeCompleter: CompleterData
  public grades: ConstData[] = CONTACT_GRADES

  public schoolCompleter: CompleterData
  public schools: ConstData[] = SCHOOLS

  public marketingSourceSearchStr: string
  public marketingSourceCompleter: CompleterData
  public marketingSources: ConstData[] = CONTACT_MARKETING_SOURCES

  public guardianSearchStr: string
  public guardianCompleter: CompleterData

  constructor(
    private store: Store<AppState>,
    private contactEffects: ContactEffects,
    private modalService: ModalService,
    private completerService: CompleterService,
    public constantsHelpers: ConstantsHelpers,
  ) {
  }

  ngOnInit() {
    this.contactsStore = this.store.select("contactsReducer")

    this.contactsStore.subscribe((s: ContactsStore) => {
      this.contact = s.editableContact
    })

    this.setupEffectEvents()

    this.salutationCompleter =
      this.completerService.local(this.salutations, "title", "title")
    this.gendersCompleter =
      this.completerService.local(this.genders, "title", "title")
    this.stateCompleter =
      this.completerService.local(this.states, "title", "title")
    this.phoneLabelCompleter =
      this.completerService.local(this.phoneLabels, "title", "title")
    this.typesCompleter =
      this.completerService.local(this.types, "title", "title")
    this.instalmentsCompleter =
      this.completerService.local(this.instalments, "title", "title")
    this.gradeCompleter =
      this.completerService.local(this.grades, "title", "title")
    this.schoolCompleter =
      this.completerService.local(this.schools, "title", "title")
    this.marketingSourceCompleter =
      this.completerService.local(this.marketingSources, "title", "title")
    this.guardianCompleter =
      this.completerService.remote(this.guardianUrl, "fullName", "fullName")
  }

  clickedSubmitForm(e: MouseEvent) {
    e.preventDefault()

    this.store.dispatch({ type: RESET_CONTACT_ERRORS, payload: this.contact })

    if (this.isInEditMode) {
      this.store.dispatch({ type: UPDATE_CONTACT, payload: this.contact })
    } else {
      this.store.dispatch({ type: CREATE_CONTACT, payload: this.contact })
    }
  }

  clickedAddPhone() {
    this.contact.phones.push(new Phone())
  }

  clickedAddAddress() {
    this.contact.addresses.push(new Address())
  }

  clickedDeleteAddress(address: Address) {
    this.contact.addresses.splice(
      this.contact.addresses.indexOf(address), 1)
  }

  clickedDeletePhone(phone: Phone) {
    this.contact.phones.splice(
      this.contact.phones.indexOf(phone), 1)
  }

  isAddressPersisted(address: Address): boolean {
    return !!address.id
  }

  isPhonePersisted(phone: Phone): boolean {
    return !!phone.id
  }

  selectedSalutation(e: CompleterItem) {
    if (e) { this.contact.salutation = e.originalObject.value }
  }

  selectedGender(e: CompleterItem) {
    if (e) { this.contact.gender = parseInt(e.originalObject.value) }
  }

  selectedState(address: Address, e: CompleterItem) {
    if (e) { address.state = e.originalObject.value }
  }

  selectedPhoneLabel(phone: Phone, e: CompleterItem) {
    if (e) { phone.label = e.originalObject.value }
  }

  selectedType(e: CompleterItem) {
    if (e) { this.contact.contactType = parseInt(e.originalObject.value) }
  }

  selectedInstalments(e: CompleterItem) {
    if (e) { this.contact.instalments = parseInt(e.originalObject.value) }
  }

  selectedGrade(e: CompleterItem) {
    if (e) { this.contact.grade = parseInt(e.originalObject.value) }
  }

  selectedSchool(e: CompleterItem) {
    if (e) { this.contact.school = e.originalObject.value }
  }

  selectedMarketingSource(e: CompleterItem) {
    if (e) { this.contact.marketingSource = e.originalObject.value }
  }

  selectedGuardian(e: CompleterItem) {
    if (e && e.originalObject) {
      const guardian          = Contact.clone(e.originalObject)
      this.contact.guardianId = guardian.id
    }
  }

  isContactType(contactType: number) {
    return this.contact.contactType === contactType
  }

  setupEffectEvents() {
    Observable.merge(this.contactEffects.createContact,
      this.contactEffects.updateContact)
      .subscribe((action: Action) => {
        switch(action.type) {
          case ADD_CONTACT:
            this.modalService.closeModal()
            break
          case REPLACE_CONTACT:
            this.modalService.closeModal()
            break }
      })
  }

  getHeader() {
    return this.isInEditMode ? `Editing ${this.contact.fullName}` : "New contact"
  }

  ngOnDestroy() {
  }

}



// WEBPACK FOOTER //
// /Users/jason/src/matrix/client/src/app/components/contact-form.ts